import React, { useState } from "react";
import { T } from "../theme/tokens.js";
import { Panel, Btn, Chip, Modal, Field, Labelled, Notice, Empty } from "../components/ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { fmtK } from "../lib/format.js";

/* Creating a contract only captures the header here. The USVI baseline
   risks and the compliance checklist are added by the database itself when
   the row lands, so a contract created any other way, by import or by
   document analysis later, comes out identical. */
export default function Contracts({ contracts, query, onCreated }) {
  const [creating, setCreating] = useState(false);

  const shown = contracts.filter((c) =>
    !query ||
    [c.name, c.client, c.contract_number].join(" ").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Btn kind="gold" onClick={() => setCreating(true)}>New contract</Btn>
      </div>

      {shown.length === 0 ? (
        <Panel>
          <Empty text={query ? `No contracts match "${query}".` : "No contracts yet."} />
        </Panel>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 14 }}>
          {shown.map((c) => (
            <div key={c.id} style={{
              background: T.panel, border: `1px solid ${T.line}`,
              borderTop: `3px solid ${T.navy}`, borderRadius: 4, padding: 18,
            }}>
              <div style={{ fontWeight: 700, fontSize: 15.5, color: T.navy }}>{c.name}</div>
              <div style={{ fontSize: 13, color: T.soft, marginBottom: 10 }}>
                {c.client || "No client set"}{c.contract_number ? ` · ${c.contract_number}` : ""}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {c.contract_type && <Chip color={T.steel}>{c.contract_type}</Chip>}
                {(c.not_to_exceed || c.value) && (
                  <Chip color={T.navy}>{fmtK(c.not_to_exceed || c.value)} ceiling</Chip>
                )}
                <Chip color={T.steel}>Net {c.payment_terms_days || 60}</Chip>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <NewContract
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); onCreated(id); }}
        />
      )}
    </div>
  );
}

const FIELDS = [
  ["name", "Contract or program name", "text", true],
  ["client", "Client or agency", "text"],
  ["contract_number", "Contract number", "text"],
  ["contract_type", "Contract type", "text"],
  ["value", "Contract value ($)", "number"],
  ["not_to_exceed", "Not to exceed ($)", "number"],
  ["start_date", "Start date", "date"],
  ["end_date", "End date", "date"],
  ["payment_terms_days", "Payment terms (net days)", "number"],
];

function NewContract({ onClose, onCreated }) {
  const [draft, setDraft] = useState({ payment_terms_days: 60 });
  const [workstreamLabel, setWorkstreamLabel] = useState("Workstream");
  const [siteLabel, setSiteLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setBusy(true);
    setError("");
    const payload = {
      ...draft,
      value: draft.value === "" ? null : draft.value,
      not_to_exceed: draft.not_to_exceed === "" ? null : draft.not_to_exceed,
      start_date: draft.start_date || null,
      end_date: draft.end_date || null,
      payment_terms_days: draft.payment_terms_days || 60,
      workstream_label: workstreamLabel.trim() || "Workstream",
      site_label: siteLabel.trim() || null,
    };
    const { data, error } = await supabase
      .from("contracts").insert(payload).select("id").single();
    if (error) { setError(friendlyError(error)); setBusy(false); return; }
    onCreated(data.id);
  };

  return (
    <Modal title="New contract" onClose={onClose} wide>
      <div style={{ display: "grid", gap: 12 }}>
        {FIELDS.map(([key, label, type, req]) => (
          <Labelled key={key} label={label}>
            <Field f={{ type, req }} value={draft[key]} onChange={(v) => setDraft({ ...draft, [key]: v })} />
          </Labelled>
        ))}

        <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 12 }}>
          <Labelled
            label="What this contract calls its areas of work"
            hint="WAPA calls them workstreams. A contract organised by phase would say Stage or Phase. This word is used on every dropdown, column and report heading for this contract."
          >
            <Field f={{ type: "text" }} value={workstreamLabel} onChange={setWorkstreamLabel}
              placeholder="Workstream" />
          </Labelled>
        </div>

        <Labelled
          label="What this contract calls its locations (optional)"
          hint="VIDE tracks by School. WAPA tracks by Island or Site. Leave blank if this contract has no location dimension."
        >
          <Field f={{ type: "text" }} value={siteLabel} onChange={setSiteLabel}
            placeholder="School, Island, Site" />
        </Labelled>

        <Notice>
          The actual list of {workstreamLabel.toLowerCase() || "workstream"} names starts
          empty and is set from the contract itself, either by hand or by the document
          analysis in a later phase. Nothing is pre-filled with another contract's names.
        </Notice>

        {error && <Notice kind="error">{error}</Notice>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={busy || !draft.name}>
            {busy ? "Creating..." : "Create contract"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
